import { useEffect, useState } from "react";
import { Modal, Button } from "antd";
import { DownloadIcon, FileText, RefreshCw } from "lucide-react";
import PropTypes from "prop-types";
import apiClient from "../../../../../helpers/apiClient";
import CheckInAgreementModal from "../../../../checkin/components/CheckInAgreementModal";
import { useToast } from "../../../../../contexts/ToastContext";

const RentalAgreementCard = ({ resDetails, t, onRefresh }) => {
    const toast = useToast();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
    const [agreementUrl, setAgreementUrl] = useState(resDetails?.agreementDocUrl || "");
    const [isGenerating, setIsGenerating] = useState(false);

    useEffect(() => {
        setAgreementUrl(resDetails?.agreementDocUrl || "");
    }, [resDetails?.agreementDocUrl]);

    const handleOpenModal = () => {
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const handleGenerateAgreement = async () => {
        if (!resDetails?.id) return;

        try {
            setIsGenerating(true);
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const response = await apiClient.post(
                `/api/agreement/generate/${resDetails.id}`,
                { timezone },
                {
                    responseType: "blob",
                    params: { timezone },
                },
            );

            const persistedUrl = response.headers?.["x-agreement-doc-url"] || response.headers?.["X-Agreement-Doc-Url"];

            if (persistedUrl) {
                setAgreementUrl(persistedUrl);
            } else {
                const blob = new Blob([response.data], { type: "application/pdf" });
                setAgreementUrl(URL.createObjectURL(blob));
            }

            toast.success({ message: t("generateAgreementSuccess") });
            onRefresh?.();
            setIsModalOpen(true);
        } catch (error) {
            // console.error("Failed to generate rental agreement:", error);
            toast.error({ message: t("generateAgreementError") });
        } finally {
            setIsGenerating(false);
        }
    };

    if (!agreementUrl) {
        return (
            <>
                <div className="mt-4 flex flex-col gap-2">
                    <div className="text-gray-500">{t("noRentalAgreement")}</div>
                    <Button type="primary" onClick={() => setIsGenerateModalOpen(true)} icon={<FileText size={16} />}>
                        {t("generateAgreement")}
                    </Button>
                </div>
                <CheckInAgreementModal
                    open={isGenerateModalOpen}
                    reservation={resDetails}
                    onCancel={() => setIsGenerateModalOpen(false)}
                    onAgreeAndContinue={async (agreementDocUrl) => {
                        setAgreementUrl(agreementDocUrl);
                        setIsGenerateModalOpen(false);
                        toast.success({ message: t("generateAgreementSuccess") });
                        onRefresh?.();
                    }}
                    primaryActionLabel={t("generateAgreement")}
                    signatureLabel={t("generateAgreementRenterSignatureLabel")}
                />
            </>
        );
    }

    return (
        <>
            <div className="mt-4 flex flex-wrap items-center gap-3">
                <button
                    onClick={handleOpenModal}
                    className="flex items-center gap-2 text-[#5D5DFF] hover:text-[#4d4dff] transition-colors font-semibold text-sm">
                    <FileText className="w-4 h-4" />
                    <span className="text-sm">{t("rentalAgreement")}</span>
                </button>
                <Button
                    size="small"
                    loading={isGenerating}
                    onClick={handleGenerateAgreement}
                    icon={<RefreshCw size={14} />}>
                    {t("regenerateAgreement")}
                </Button>
            </div>
            <Modal
                title={<span className="text-lg font-medium">{t("rentalAgreement")}</span>}
                open={isModalOpen}
                onCancel={handleCloseModal}
                footer={[
                    <Button
                        key="download"
                        type="primary"
                        icon={<DownloadIcon size={16} />}
                        onClick={() => {
                            const link = document.createElement("a");
                            link.href = agreementUrl;
                            link.download = `rental-agreement-${resDetails.id || "document"}.pdf`;
                            link.target = "_blank";
                            link.click();
                        }}>
                        {t("download")}
                    </Button>,
                    <Button key="close" onClick={handleCloseModal}>
                        {t("close")}
                    </Button>,
                ]}
                width={900}
                style={{ top: 20 }}
                destroyOnClose>
                <div className="w-full" style={{ height: "calc(100vh - 200px)", padding: 0 }}>
                    <div className="w-full h-full min-h-[600px] bg-gray-50">
                        <iframe
                            src={`${agreementUrl}#toolbar=1&navpanes=1&scrollbar=1`}
                            width="100%"
                            height="100%"
                            className="w-full h-full border-0"
                            title="Rental Agreement PDF Viewer"
                            style={{ minHeight: "600px" }}
                            allow="fullscreen"
                        />
                    </div>
                </div>
            </Modal>
        </>
    );
};

RentalAgreementCard.propTypes = {
    resDetails: PropTypes.object,
    t: PropTypes.func.isRequired,
    onRefresh: PropTypes.func,
};

export default RentalAgreementCard;
